import { useState, useEffect, useRef } from 'react';
import type { DropdownPosition, DropdownPositionConfig } from '../types/dropdown.types';

interface UseDropdownPositionOptions extends DropdownPositionConfig {
  isOpen: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  dropdownRef?: React.RefObject<HTMLElement>;
  offsetY?: number;
}

interface UseDropdownPositionReturn {
  position: DropdownPosition | null;
  actualDirection: 'up' | 'down';
}

export function useDropdownPosition({
  isOpen,
  anchorRef,
  dropdownRef,
  position = 'left',
  direction = 'down',
  offsetY = 4
}: UseDropdownPositionOptions): UseDropdownPositionReturn {
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const [actualDirection, setActualDirection] = useState<'up' | 'down'>(direction);

  const calculatePosition = useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const anchor = anchorRef.current;
      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const dropdown = dropdownRef?.current;
      const dropdownHeight = dropdown?.offsetHeight || 320;
      const dropdownWidth = dropdown?.offsetWidth || 300;
      
      let verticalPosition: { top?: number; bottom?: number };
      let calculatedDirection: 'up' | 'down' = direction;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (direction === 'up') {
        if (spaceAbove >= dropdownHeight + offsetY) {
          verticalPosition = { bottom: viewportHeight - rect.top + offsetY };
          calculatedDirection = 'up';
        } else if (spaceBelow >= dropdownHeight + offsetY) {
          verticalPosition = { top: rect.bottom + offsetY };
          calculatedDirection = 'down';
        } else {
          verticalPosition = { top: Math.min(rect.bottom + offsetY, viewportHeight - dropdownHeight - offsetY) };
          calculatedDirection = 'down';
        }
      } else {
        if (spaceBelow >= dropdownHeight + offsetY) {
          verticalPosition = { top: rect.bottom + offsetY };
          calculatedDirection = 'down';
        } else if (spaceAbove >= dropdownHeight + offsetY) {
          verticalPosition = { bottom: viewportHeight - rect.top + offsetY };
          calculatedDirection = 'up';
        } else {
          verticalPosition = { top: Math.min(rect.bottom + offsetY, viewportHeight - dropdownHeight - offsetY) };
          calculatedDirection = 'down';
        }
      }
      
      setActualDirection(calculatedDirection);
      
      let horizontalPosition: { left?: number; right?: number };
      
      if (position === 'right') {
        if (rect.right - dropdownWidth < 0) {
          horizontalPosition = { left: Math.max(8, rect.left) };
        } else {
          horizontalPosition = { right: viewportWidth - rect.right };
        }
      } else {
        if (rect.left + dropdownWidth > viewportWidth) {
          horizontalPosition = { right: Math.max(8, viewportWidth - rect.right) };
        } else {
          horizontalPosition = { left: rect.left };
        }
      }
      
      setDropdownPosition({
        ...verticalPosition,
        ...horizontalPosition
      });
    } else if (!isOpen) {
      setDropdownPosition(null);
    }
  }, [isOpen, anchorRef, dropdownRef, position, direction, offsetY]);
  
  useEffect(() => {
    if (isOpen && !dropdownPosition && anchorRef?.current) {
      const timer = setTimeout(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        
        const rect = anchor.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        const dropdown = dropdownRef?.current;
        const dropdownHeight = dropdown?.offsetHeight || 320;
        const dropdownWidth = dropdown?.offsetWidth || 300;
        
        // Calculate available space
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // Determine vertical position with auto-flip
        let verticalPosition: { top?: number; bottom?: number };
        let calculatedDirection: 'up' | 'down' = direction;
        
        if (direction === 'up') {
          if (spaceAbove >= dropdownHeight + offsetY) {
            verticalPosition = { bottom: viewportHeight - rect.top + offsetY };
            calculatedDirection = 'up';
          } else if (spaceBelow >= dropdownHeight + offsetY) {
            verticalPosition = { top: rect.bottom + offsetY };
            calculatedDirection = 'down';
          } else {
            verticalPosition = { top: Math.min(rect.bottom + offsetY, viewportHeight - dropdownHeight - offsetY) };
            calculatedDirection = 'down';
          }
        } else {
          if (spaceBelow >= dropdownHeight + offsetY) {
            verticalPosition = { top: rect.bottom + offsetY };
            calculatedDirection = 'down';
          } else if (spaceAbove >= dropdownHeight + offsetY) {
            verticalPosition = { bottom: viewportHeight - rect.top + offsetY };
            calculatedDirection = 'up';
          } else {
            verticalPosition = { top: Math.min(rect.bottom + offsetY, viewportHeight - dropdownHeight - offsetY) };
            calculatedDirection = 'down';
          }
        }
        
        setActualDirection(calculatedDirection);
        
        // Determine horizontal position
        let horizontalPosition: { left?: number; right?: number };
        
        if (position === 'right') {
          if (rect.right - dropdownWidth < 0) {
            horizontalPosition = { left: Math.max(8, rect.left) };
          } else {
            horizontalPosition = { right: viewportWidth - rect.right };
          }
        } else {
          if (rect.left + dropdownWidth > viewportWidth) {
            horizontalPosition = { right: Math.max(8, viewportWidth - rect.right) };
          } else {
            horizontalPosition = { left: rect.left };
          }
        }
        
        setDropdownPosition({
          ...verticalPosition,
          ...horizontalPosition
        });
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, dropdownPosition, anchorRef, dropdownRef, position, direction, offsetY]);
  
  useEffect(() => {
    if (!isOpen) return;
    
    const handleResize = () => {
      setDropdownPosition(null);
      setTimeout(() => {
        if (anchorRef?.current && dropdownRef?.current) {
          const anchor = anchorRef.current;
          const rect = anchor.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          const dropdown = dropdownRef.current;
          const dropdownHeight = dropdown.offsetHeight || 320;
          const dropdownWidth = dropdown.offsetWidth || 300;
          
          let verticalPosition: { top?: number; bottom?: number };
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          if (spaceBelow >= dropdownHeight + offsetY) {
            verticalPosition = { top: rect.bottom + offsetY };
          } else if (spaceAbove >= dropdownHeight + offsetY) {
            verticalPosition = { bottom: viewportHeight - rect.top + offsetY };
          } else {
            verticalPosition = { top: Math.min(rect.bottom + offsetY, viewportHeight - dropdownHeight - offsetY) };
          }
          
          let horizontalPosition: { left?: number; right?: number };
          
          if (rect.left + dropdownWidth > viewportWidth) {
            horizontalPosition = { right: Math.max(8, viewportWidth - rect.right) };
          } else {
            horizontalPosition = { left: rect.left };
          }
          
          setDropdownPosition({
            ...verticalPosition,
            ...horizontalPosition
          });
        }
      }, 0);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, anchorRef, dropdownRef]);

  return {
    position: dropdownPosition,
    actualDirection
  };
}

